import { Trash2 } from 'lucide-react';
import { useMemo, useState } from 'react';
import type { FrameLogEntry } from '@/session/store';
import { useSession } from '@/session/store';

export function Frames() {
  const frames = useSession((state) => state.frames);
  const framesDropped = useSession((state) => state.framesDropped);
  const audioFramesSent = useSession((state) => state.audioFramesSent);
  const clearFrames = useSession((state) => state.clearFrames);

  const [filter, setFilter] = useState('');

  const shown = useMemo(() => {
    const needle = filter.trim().toLowerCase();
    // The gap is measured against the frame before it in the log, not before it in the filtered
    // view: a filter is for finding frames, and it would otherwise silently change the numbers.
    const timed = frames.map((frame, i) => ({
      frame,
      sincePrevMs: i === 0 ? null : frame.atMs - frames[i - 1].atMs,
    }));
    const matching = needle
      ? timed.filter(
          ({ frame }) =>
            frame.type.toLowerCase().includes(needle) || frame.body.toLowerCase().includes(needle),
        )
      : timed;
    return [...matching].reverse();
  }, [frames, filter]);

  return (
    <>
      <div className="dbg-tools">
        <input
          value={filter}
          placeholder="filter by type or content"
          spellCheck={false}
          autoComplete="off"
          onChange={(event) => setFilter(event.target.value)}
          className="dbg-input flex-1"
        />
        <button
          type="button"
          aria-label="Clear the frame log"
          onClick={clearFrames}
          disabled={frames.length === 0}
          className="dbg-icon"
        >
          <Trash2 className="size-3.5" />
        </button>
      </div>

      {/* Mic frames are counted rather than logged: fifteen a second would bury everything else. */}
      <p className="dbg-note dbg-count">
        {frames.length} logged · {audioFramesSent} audio frames sent
        {framesDropped > 0 && ` · ${framesDropped} dropped from the start`}
      </p>

      <div className="dbg-scroll pt-2">
        {shown.length === 0 ? (
          <p className="dbg-empty">Nothing matching yet.</p>
        ) : (
          shown.map(({ frame, sincePrevMs }) => (
            <Frame key={frame.id} frame={frame} sincePrevMs={sincePrevMs} />
          ))
        )}
      </div>
    </>
  );
}

function Frame({ frame, sincePrevMs }: { frame: FrameLogEntry; sincePrevMs: number | null }) {
  const inbound = frame.direction === 'in';

  return (
    <details className="dbg-disclosure dbg-frame">
      <summary className="dbg-summary">
        <span className="dbg-chevron" aria-hidden="true" />
        {/* Direction as a glyph rather than a word: it is scanned down a column, never read. */}
        <span
          className="shrink-0"
          style={{ color: inbound ? 'var(--color-phase-asr)' : 'var(--color-phase-tool)' }}
          title={inbound ? 'from the server' : 'to the server'}
        >
          {inbound ? '↓' : '↑'}
        </span>
        <span
          className="shrink-0 text-[11.5px] font-semibold"
          style={{ color: frame.known ? 'var(--dbg-text)' : 'var(--color-phase-tts)' }}
        >
          {frame.type}
        </span>
        <span className="dbg-preview flex-1">{frame.body}</span>
        <span className="dbg-note shrink-0" title="since the previous frame in the log">
          {sincePrevMs === null ? '—' : `+${sincePrevMs}ms`}
        </span>
        <span className="dbg-note shrink-0">
          {new Date(frame.atMs).toLocaleTimeString([], { hour12: false })}
        </span>
      </summary>
      <pre className="dbg-pre">{frame.body}</pre>
    </details>
  );
}
